import React, { useState, useEffect } from 'react';
import {
    CurrencyPoundIcon,
    ShoppingCartIcon,
    CheckCircleIcon,
    MinusIcon,
    PlusIcon,
    TagIcon
} from '@heroicons/react/24/outline';
import LayoutWrapper from '../components/LayoutWrapper';
import Helper from '../helpers';
import config from '../config';

const baseUrl = config.apiBaseUrl;

const BuyTokens = () => {
    const [venueKey, setVenueKey] = useState('');
    const [tokenPrice, setTokenPrice] = useState(null);
    const [venueName, setVenueName] = useState('');
    const [discountTiers, setDiscountTiers] = useState([]);
    const [quantity, setQuantity] = useState(1);
    const [loading, setLoading] = useState(true);
    const [purchasing, setPurchasing] = useState(false);
    const [error, setError] = useState('');

    const QUICK_QUANTITIES = [1, 5, 10, 20];

    useEffect(() => {
        const init = async () => {
            const key = await Helper.getUserVenueId();
            if (key) setVenueKey(key);
        };
        init();
    }, []);

    useEffect(() => {
        if (!venueKey) return;

        const fetchTokenPrice = async () => {
            setLoading(true);
            try {
                const token = await Helper.getCookie('wh-jwt');
                const response = await fetch(`${baseUrl}/api/Venue/${venueKey}/token-price`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    credentials: 'include'
                });

                if (response.ok) {
                    const data = await response.json();
                    setTokenPrice(data.tokenPrice);
                    setVenueName(data.venueName);
                    setDiscountTiers(data.discountTiers || []);
                } else {
                    setError('Failed to load token pricing');
                }
            } catch (err) {
                console.error('Error fetching token price:', err);
                setError('Failed to load token pricing');
            } finally {
                setLoading(false);
            }
        };

        fetchTokenPrice();
    }, [venueKey]);

    // Find the best discount percent for the current quantity
    const getBestDiscount = (qty) => {
        if (!discountTiers || discountTiers.length === 0) return 0;
        const applicable = discountTiers
            .filter(t => qty >= t.minQuantity)
            .sort((a, b) => b.discountPercent - a.discountPercent);
        return applicable.length > 0 ? applicable[0].discountPercent : 0;
    };

    const activeDiscount = getBestDiscount(quantity);
    const baseTotal = tokenPrice != null ? tokenPrice * quantity : null;
    const discountedTotal = baseTotal != null && activeDiscount > 0
        ? Math.max(0, baseTotal * (1 - activeDiscount / 100))
        : baseTotal;
    const totalCost = discountedTotal != null ? discountedTotal.toFixed(2) : null;
    const baseTotalDisplay = baseTotal != null ? baseTotal.toFixed(2) : null;

    const handleQuantityChange = (delta) => {
        setQuantity(prev => Math.max(1, prev + delta));
    };

    const handleManualQuantity = (e) => {
        const val = parseInt(e.target.value, 10);
        if (!isNaN(val) && val >= 1) setQuantity(val);
    };

    const handlePurchase = async () => {
        if (!venueKey || quantity < 1) return;

        setPurchasing(true);
        setError('');
        try {
            const token = await Helper.getCookie('wh-jwt');
            const response = await fetch(`${baseUrl}/api/Order`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({
                    type: 0, // OneOff
                    venueKey: venueKey,
                    description: `${quantity} Token${quantity > 1 ? 's' : ''} Purchase`,
                    qty: quantity,
                    currency: 'GBP'
                })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.paymentUrl) {
                    window.location.href = data.paymentUrl;
                } else {
                    setError('Payment URL not received. Please try again.');
                }
            } else {
                const errorData = await response.json().catch(() => ({}));
                setError(errorData.message || errorData || 'Failed to initiate payment. Please try again.');
            }
        } catch (err) {
            console.error('Error purchasing tokens:', err);
            setError('Failed to initiate payment. Please try again.');
        } finally {
            setPurchasing(false);
        }
    };

    if (loading) {
        return (
            <LayoutWrapper title="Buy Tokens">
                <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-400"></div>
                </div>
            </LayoutWrapper>
        );
    }

    return (
        <LayoutWrapper
            title="Buy Tokens"
            subtitle={`Purchase additional tokens for ${venueName || 'your venue'}`}
        >
            <div className="max-w-2xl mx-auto space-y-6">
                {error && (
                    <div className="p-4 bg-red-900/30 border border-red-500/30 rounded-lg text-red-400">
                        {error}
                    </div>
                )}

                {/* Token price info */}
                <div className="card p-6 bg-gradient-to-br from-emerald-900/30 to-gray-900">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-300 text-sm font-medium">Price per Token</p>
                            <p className="text-4xl font-bold text-emerald-400 mt-1">
                                £{tokenPrice != null ? Number(tokenPrice).toFixed(2) : '—'}
                            </p>
                            <p className="text-gray-500 text-sm mt-1">{venueName}</p>
                        </div>
                        <CurrencyPoundIcon className="h-16 w-16 text-emerald-400 opacity-30" />
                    </div>
                </div>

                {/* Volume discount tiers */}
                {discountTiers.length > 0 && (
                    <div className="card p-5">
                        <div className="flex items-center gap-2 mb-3">
                            <TagIcon className="h-5 w-5 text-yellow-400" />
                            <h3 className="text-white font-semibold">Volume Discounts</h3>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {[...discountTiers]
                                .sort((a, b) => a.minQuantity - b.minQuantity)
                                .map(tier => {
                                    const isActive = quantity >= tier.minQuantity;
                                    return (
                                        <div
                                            key={tier.minQuantity}
                                            className={`flex items-center justify-between px-4 py-2 rounded-lg border transition-all ${
                                                isActive
                                                    ? 'bg-yellow-900/30 border-yellow-500/50 text-yellow-300'
                                                    : 'bg-gray-800/50 border-gray-700 text-gray-400'
                                            }`}
                                        >
                                            <span className="text-sm">Buy {tier.minQuantity}+ tokens</span>
                                            <span className="font-bold text-sm">{tier.discountPercent}% OFF</span>
                                        </div>
                                    );
                                })}
                        </div>
                    </div>
                )}

                {/* Quantity selector */}
                <div className="card p-6">
                    <h3 className="text-white font-semibold mb-4">Select Quantity</h3>

                    {/* Quick select buttons */}
                    <div className="grid grid-cols-4 gap-3 mb-6">
                        {QUICK_QUANTITIES.map(q => {
                            const qDiscount = getBestDiscount(q);
                            return (
                                <button
                                    key={q}
                                    onClick={() => setQuantity(q)}
                                    className={`py-3 rounded-lg font-semibold transition-all relative ${
                                        quantity === q
                                            ? 'bg-emerald-600 text-white border-2 border-emerald-400'
                                            : 'bg-gray-800 text-gray-300 border-2 border-transparent hover:border-emerald-600/50'
                                    }`}
                                >
                                    {q} {q === 1 ? 'Token' : 'Tokens'}
                                    {qDiscount > 0 && (
                                        <span className="absolute -top-2 -right-2 bg-yellow-500 text-black text-xs font-bold px-1.5 py-0.5 rounded-full">
                                            -{qDiscount}%
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    {/* Manual quantity input */}
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => handleQuantityChange(-1)}
                            disabled={quantity <= 1}
                            className="p-2 rounded-lg bg-gray-800 text-gray-300 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                            <MinusIcon className="h-5 w-5" />
                        </button>
                        <input
                            type="number"
                            min="1"
                            value={quantity}
                            onChange={handleManualQuantity}
                            className="w-24 text-center bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                        />
                        <button
                            onClick={() => handleQuantityChange(1)}
                            className="p-2 rounded-lg bg-gray-800 text-gray-300 hover:bg-gray-700 transition-colors"
                        >
                            <PlusIcon className="h-5 w-5" />
                        </button>
                        <span className="text-gray-400 text-sm">tokens</span>
                    </div>

                    {/* Active discount banner */}
                    {activeDiscount > 0 && (
                        <div className="mt-4 flex items-center gap-2 p-3 bg-yellow-900/30 border border-yellow-500/30 rounded-lg text-yellow-300">
                            <TagIcon className="h-4 w-4 flex-shrink-0" />
                            <span className="text-sm font-medium">
                                🎉 {activeDiscount}% volume discount applied!
                            </span>
                        </div>
                    )}
                </div>

                {/* Order summary */}
                <div className="card p-6">
                    <h3 className="text-white font-semibold mb-4">Order Summary</h3>
                    <div className="space-y-3">
                        <div className="flex justify-between text-gray-300">
                            <span>Tokens</span>
                            <span>{quantity}</span>
                        </div>
                        <div className="flex justify-between text-gray-300">
                            <span>Price per Token</span>
                            <span>£{tokenPrice != null ? Number(tokenPrice).toFixed(2) : '—'}</span>
                        </div>
                        {activeDiscount > 0 && (
                            <>
                                <div className="flex justify-between text-gray-400">
                                    <span>Subtotal</span>
                                    <span className="line-through">£{baseTotalDisplay ?? '—'}</span>
                                </div>
                                <div className="flex justify-between text-yellow-400">
                                    <span>Volume Discount ({activeDiscount}% off)</span>
                                    <span>-£{(baseTotal - discountedTotal).toFixed(2)}</span>
                                </div>
                            </>
                        )}
                        <div className="border-t border-gray-700 pt-3 flex justify-between text-white font-bold text-lg">
                            <span>Total</span>
                            <span className="text-emerald-400">£{totalCost ?? '—'}</span>
                        </div>
                    </div>
                </div>

                {/* What happens info */}
                <div className="card p-4 bg-gray-800/50">
                    <div className="flex items-start gap-3">
                        <CheckCircleIcon className="h-5 w-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-gray-400">
                            You will be redirected to our secure payment provider (GoCardless) to complete your purchase.
                            Tokens will be added to your account once the payment is confirmed.
                        </p>
                    </div>
                </div>

                {/* Purchase button */}
                <button
                    onClick={handlePurchase}
                    disabled={purchasing || tokenPrice == null || quantity < 1}
                    className="w-full py-4 rounded-lg font-semibold text-white bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-3"
                >
                    {purchasing ? (
                        <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            Processing...
                        </>
                    ) : (
                        <>
                            <ShoppingCartIcon className="h-5 w-5" />
                            Buy {quantity} Token{quantity !== 1 ? 's' : ''} — £{totalCost ?? '—'}
                            {activeDiscount > 0 && (
                                <span className="text-xs bg-yellow-500 text-black px-1.5 py-0.5 rounded font-bold">
                                    {activeDiscount}% OFF
                                </span>
                            )}
                        </>
                    )}
                </button>
            </div>
        </LayoutWrapper>
    );
};

export default BuyTokens;
